/**
 * Public Shop navigation — single source of truth for product discovery links.
 *
 * Used by PublicNav (desktop Menu + mobile accordion) and PublicFooter so
 * labels, paths, icons, and descriptors cannot drift across surfaces.
 *
 * Business rules (PBI #1063):
 * - Order is E-Cards → Mailed Cards → Flowers.
 * - Icons/colors come from serviceRegistry (no ad-hoc icon maps).
 * - Public labels use plural marketing forms (E-Cards / Mailed Cards).
 * - E-Cards descriptor leads with "Free" (no per-card cost); subscription
 *   qualifiers stay on Pricing/FAQ, not in this short descriptor.
 * - SHOP_NAV_HIGHLIGHT drives the disposable "New" badge — clear or set
 *   highlightKey to null when mailed cards are no longer new.
 */

import type { Icon } from '@tabler/icons-react';
import { ServiceType } from '../types';
import { getServiceConfigByType } from '../config/serviceRegistry';
import { PUBLIC_ROUTES } from './routes';

/** Stable keys for shop products (analytics + highlight matching). */
export const ShopNavProductKey = {
  ECards: 'eCards',
  MailedCards: 'mailedCards',
  Flowers: 'flowers',
} as const;

export type ShopNavProductKey =
  (typeof ShopNavProductKey)[keyof typeof ShopNavProductKey];

/**
 * One public Shop menu / footer product entry.
 */
export interface ShopNavItem {
  key: ShopNavProductKey;
  /** Public marketing label (plural where appropriate). */
  label: string;
  path: string;
  /** One-line descriptor distinguishing digital vs physical / paid. */
  descriptor: string;
  icon: Icon;
  color: string;
  /** Maps to ServiceType for analytics payloads. */
  serviceType: ServiceType;
}

const eCard = getServiceConfigByType(ServiceType.ECard);
const mailedCard = getServiceConfigByType(ServiceType.MailedCard);
const flowers = getServiceConfigByType(ServiceType.Flowers);

/**
 * Ordered Shop product links for public nav and footer.
 */
export const SHOP_NAV_ITEMS: readonly ShopNavItem[] = [
  {
    key: ShopNavProductKey.ECards,
    label: 'E-Cards',
    path: PUBLIC_ROUTES.cards,
    descriptor: 'Free — arrives by email',
    icon: eCard.icon,
    color: eCard.color,
    serviceType: ServiceType.ECard,
  },
  {
    key: ShopNavProductKey.MailedCards,
    label: 'Mailed Cards',
    path: PUBLIC_ROUTES.mailedCards,
    descriptor: 'A real card posted to their door',
    icon: mailedCard.icon,
    color: mailedCard.color,
    serviceType: ServiceType.MailedCard,
  },
  {
    key: ShopNavProductKey.Flowers,
    label: 'Flowers',
    path: PUBLIC_ROUTES.flowers,
    descriptor: 'Local florist delivery, timed to the day',
    icon: flowers.icon,
    color: flowers.color,
    serviceType: ServiceType.Flowers,
  },
] as const;

/**
 * Disposable "New" highlight for a Shop product.
 * Set highlightKey to null to remove the badge in one edit.
 */
export const SHOP_NAV_HIGHLIGHT = {
  /** Which product shows the New badge; null disables the highlight. */
  highlightKey: ShopNavProductKey.MailedCards as ShopNavProductKey | null,
  /** Badge label copy. */
  badgeLabel: 'New',
} as const;

/** True when the given path is one of the Shop product routes. */
export function isShopNavPath(pathname: string): boolean {
  return SHOP_NAV_ITEMS.some((item) => item.path === pathname);
}

/** True when the item should show the New badge. */
export function isShopNavHighlighted(key: ShopNavProductKey): boolean {
  return SHOP_NAV_HIGHLIGHT.highlightKey === key;
}
